<!--
 * @Description: 确认开票。确认后流程结束
 * @Date: 2025-06-23 10:42:08
-->
<template>
  <BaseDialog
    width="950px"
    class="form-dialog form-dialog--label120"
    title="申请开票"
    :model-value="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
  >
    <BaseTextContent>
      <BaseForm ref="formRef" label-position="right" :model="formData">
        <div>
          <div class="form-dialog__title">
            <h1>开票信息</h1>
          </div>
          <div class="form-dialog__content">
            <BaseFormItem label="发票方式" prop="invoiceMethod">
              <BaseRadioGroup v-model="formData.invoiceMethod">
                <BaseRadio disabled :value="0">纸质发票</BaseRadio>
              </BaseRadioGroup>
            </BaseFormItem>
          </div>
        </div>
        <div>
          <div class="form-dialog__title">
            <h1>发票邮寄信息</h1>
          </div>
          <!-- TODO: address字段没有地方编辑，此处为空，如果有BUG则转给产品 -->
          <BaseFormItem
            v-if="addressList.length"
            label="收件地址"
            prop="addressId"
          >
            <BaseSelect
              class="form-dialog__w280"
              placeholder="请选择"
              v-model="formData.addressId"
              :options="
                addressList.map(item => {
                  return { label: item.mailAddress, value: item.id };
                })
              "
            ></BaseSelect>
          </BaseFormItem>
          <div class="form-dialog__content" v-if="curAddress">
            <BaseFormItem label="收件人" prop="name">
              <BaseInput
                class="form-dialog__w280"
                type="text"
                placeholder="请输入"
                disabled
                v-model="curAddress.name"
              ></BaseInput>
            </BaseFormItem>
            <BaseFormItem label="联系方式" prop="phone">
              <BaseInput
                class="form-dialog__w280"
                type="text"
                placeholder="请输入"
                disabled
                v-model="curAddress.phone"
              ></BaseInput>
            </BaseFormItem>
            <BaseFormItem label="发票邮寄地址" prop="mailAddress">
              <BaseInput
                class="form-dialog__w280"
                type="text"
                placeholder="请输入"
                disabled
                v-model="curAddress.mailAddress"
              ></BaseInput>
            </BaseFormItem>
            <BaseFormItem label="电子邮箱" prop="email">
              <BaseInput
                class="form-dialog__w280"
                type="tel"
                placeholder="请输入"
                disabled
                v-model="curAddress.email"
              ></BaseInput>
            </BaseFormItem>
          </div>
        </div>
        <div>
          <div class="form-dialog__title">
            <h1>默认开票种类</h1>
          </div>
          <div class="form-dialog__content">
            <BaseFormItem label="发票类别" prop="invoiceType">
              <BaseSelect
                class="form-dialog__w280"
                placeholder="请选择"
                v-model="formData.invoiceType"
                :options="INVOICE_TYPE"
              ></BaseSelect>
            </BaseFormItem>
            <!-- v-if="items.length" 解决重新触发remoteMethod -->
            <BaseFormItem v-if="items.length" label="发票类目" prop="itemId">
              <BaseSelect
                class="form-dialog__w280"
                placeholder="请选择"
                labelKey="itemName"
                valueKey="id"
                init
                :remoteMethod="() => remoteItems(items)"
                v-model="formData.itemId"
                @change="onItemChange"
              ></BaseSelect>
            </BaseFormItem>
          </div>
        </div>
        <div>
          <div class="form-dialog__title">
            <h1>开票内容</h1>
          </div>
          <div class="form-dialog__content">
            <XrkTable
              ref="xrkTableRef"
              :requestNow="false"
              :getListAjax="getListAjax"
              :searchConfig="searchConfig"
              :tableConfig="tableConfig"
            >
              <template #tableBefore>
                <div>
                  <span style="margin-right: 15px">
                    价税合计：{{ formatter.fen2yuan(totalPrice) }}元
                  </span>
                  <!-- 不可勾选，所以只有一个 价税合计 -->
                  <!-- <span>
                  最大开票金额：{{ formatter.fen2yuan(totalPrice) }}元
                </span> -->
                </div>
              </template>
            </XrkTable>
          </div>
        </div>
      </BaseForm>
    </BaseTextContent>
    <template #footer>
      <BaseButton type="default" @click="remove">关闭</BaseButton>
      <BaseButton :click="onConfirm">确认开票</BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="tsx" name="invoice-enterprise-dialog" setup>
import { ref, onMounted, computed, watch, isRef } from 'vue';
import {
  BaseDialog,
  BaseTextContent,
  BaseButton,
  useCreateModal,
  BaseFormItem,
  BaseRadioGroup,
  BaseRadio,
  BaseSelect,
  BaseInput,
  XrkTable,
  XrkTableProps,
  useTableColumn,
  BaseTable,
  BaseForm
} from 'xrk-components';
import {
  basicInformation,
  invoiceCategory,
  contentOfMakeOutAnInvoice,
  makeOutAnInvoiceDk
} from '@V/invoice/src/data-source/apis';
import {
  BasicInformationData,
  MakeOutAnInvoiceDkParams
} from '@V/invoice/src/data-source/types';
import { INVOICE_TYPE } from '@V/invoice/src/constants/index';
import { formatter } from '@/utils';
import InvoiceWtdk4Dialog from './invoice-wtdk-4-dialog.vue';
import { NotInvoicedListItem } from '@V/invoice/src/data-source/types';
import { useCloned } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    orders: Array<NotInvoicedListItem>;
    remove?: () => {};
  }>(),
  {}
);
const emits = defineEmits<{
  (event: 'success', value: void): void;
}>();

const xrkTableRef = ref(null);
const totalPrice = ref(0);
const items = ref<BasicInformationData['itemList']>([]);
const addressList = ref<BasicInformationData['addressList']>([]);
const formData = ref<MakeOutAnInvoiceDkParams>({
  openOrderIdList: props.orders.reduce((pre, cur) => {
    const ids = cur._selections.map(item => item.openOrderId);
    [].push.apply(pre, ids);
    return pre;
  }, []),
  itemId: null,
  itemName: '',
  addressId: null,
  invoiceType: 1,
  sonList: [],
  invoiceMethod: 0
});

const curAddress = computed(() => {
  return addressList.value.find(item => item.id === formData.value.addressId);
});

const remoteItems = arr => {
  return Promise.resolve({
    data: isRef(arr) ? arr.value : arr
  });
};
// 发票类目控制列表重载
const getListAjax = async params => {
  // 发票类别、发票类目要求必传，但是内部可编辑两项，所以必传是没用的。
  const resp = await contentOfMakeOutAnInvoice({
    ...params,
    ...formData.value // 列表需要的入参包含在了form里，直接使用
  });
  // 总额
  totalPrice.value = resp.data.maximumAmountInvoiced;
  // 设置row中数据，外面的类别、类目修改后重新给row赋值与默认值
  resp.data.list.forEach((item, index) => {
    const { cloned } = useCloned(items.value);
    item._items = cloned.value;
    item._key = `${index}_${Date.now()}`;
    item.itemId = formData.value.itemId;
    item.itemName = formData.value.itemName;
    item.invoiceType = formData.value.invoiceType;
  });
  return resp;
};
const setItems = async invoiceType => {
  items.value = []; // 置空是为了让select组件重载，触发remoteMethod
  // 委托代发stateType写死
  const { data } = await invoiceCategory({ stateType: 2, invoiceType });
  items.value = data;
  // 默认选中第一个，解决列表入参该字段是必选且列表无所搜按钮的产品问题
  formData.value.itemId = data[0].id;
  formData.value.itemName = data[0].itemName;
  onItemChange(data[0].id, [data[0]]);
};

const onItemChange = (v, options) => {
  formData.value.itemName = options[0].itemName;
  xrkTableRef.value.initList();
};
const onConfirm = async () => {
  const { data } = await makeOutAnInvoiceDk({
    ...formData.value,
    sonList: xrkTableRef.value.getTableData().value.map(item => {
      const { invoiceType, itemId, itemName, userId = 'no value' } = item;
      return { invoiceType, itemId, itemName, userId };
    })
  });
  // 仅查看
  useCreateModal(InvoiceWtdk4Dialog, {
    payload: data
  });
  emits('success');
  props.remove();
};

watch(
  () => formData.value.invoiceType,
  v => {
    // 初始与监听，全部依赖于发票类目
    setItems(v);
  },
  {
    immediate: true
  }
);

onMounted(() => {
  basicInformation().then(({ data }) => {
    addressList.value = data.addressList;
    // 赋值开票地址(第一个)默认项
    formData.value.addressId =
      data.addressList.find(item => item.isDefault)?.id || null;
  });
});

const searchConfig = ref<XrkTableProps['searchConfig']>({
  hide: true,
  searchColumns: [],
  searchBtn: {
    show: false,
    text: '搜索'
  },
  resetBtn: {
    show: false,
    text: '重置'
  }
});
const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    useTableColumn({
      type: 'expand',
      fixed: 'left',
      width: 70,
      render({ row }) {
        return (
          <>
            <BaseTable
              data={row.sonList}
              columns={[
                useTableColumn({
                  label: '',
                  type: 'index',
                  width: '80px'
                }),
                useTableColumn({
                  label: '项目名称',
                  prop: 'projectName'
                }),
                useTableColumn({
                  label: '任务月度',
                  prop: 'taskAt',
                  formatter: ({ cellValue }) =>
                    formatter.date(cellValue, 'YYYY-MM')
                }),
                useTableColumn({
                  label: '开票金额',
                  prop: 'userDefinedPrice',
                  formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
                })
              ]}
            />
          </>
        );
      }
    }),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px'
    }),
    useTableColumn({
      label: '项目参与人',
      prop: 'name'
    }),
    useTableColumn({
      label: '手机号',
      prop: 'phone'
    }),
    useTableColumn({
      label: '开票金额',
      prop: 'userDefinedPrice',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '发票类别',
      prop: 'invoiceType',
      width: '180px',
      render({ row, index }) {
        return (
          <BaseSelect
            v-model={row.invoiceType}
            options={INVOICE_TYPE}
            onChange={async v => {
              // 联动类目
              const { data } = await invoiceCategory({
                stateType: 2,
                invoiceType: v
              });
              row._items = data;
              row.itemName = data[0].itemName;
              row.itemId = data[0].id;
              row._key = `${index}_${Date.now()}`;
            }}
          />
        );
      }
    }),
    useTableColumn({
      label: '发票类目',
      prop: 'itemId',
      width: '180px',
      render({ row }) {
        return (
          <BaseSelect
            labelKey="itemName"
            valueKey="id"
            key={row._key}
            init
            remoteMethod={() => remoteItems(row._items)}
            v-model={row.itemId}
            onChange={(v, option) => {
              row.itemName = option[0].itemName;
            }}
          />
        );
      }
    })
  ]
});
</script>
